'use client';

import { Geist, Geist_Mono } from "next/font/google";
import "./globals.css";
import { useState, useEffect, Suspense } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { auth, db } from '@/lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import TrafficStarsTracker from './components/TrafficStarsTracker';
import Script from 'next/script';

const geistSans = Geist({
  variable: "--font-geist-sans",
  subsets: ["latin"],
});

const geistMono = Geist_Mono({
  variable: "--font-geist-mono",
  subsets: ["latin"],
});

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const router = useRouter();
  const pathname = usePathname();
  const [user, setUser] = useState<User | null>(null);
  const [activeBottomNav, setActiveBottomNav] = useState('explore');
  const [isMounted, setIsMounted] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setIsNavigating(false);
    
    if (pathname === '/') {
      setActiveBottomNav('explore');
      // Force body/html to allow fixed positioning on landing page
      document.documentElement.style.position = 'static';
      document.body.style.position = 'static';
      document.body.style.transform = 'none';
      document.documentElement.style.transform = 'none';
    } else if (pathname.includes('/imagegenertion')) {
      setActiveBottomNav('createImage');
    } else if (pathname.includes('/myai')) {
      setActiveBottomNav('myai');
    }
  }, [pathname]);

  const handleBottomNavClick = (navItem: string) => {
    if (isNavigating) return;
    
    setActiveBottomNav(navItem);

    switch (navItem) {
      case 'explore':
        setIsNavigating(true);
        router.push('/');
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
        break;
      case 'chat':
        if (!user) {
          alert('Please sign in to access Chat');
        } else {
          // Fetch first chat from user's chat history
          const fetchFirstChat = async () => {
            try {
              const chatHistoryRef = collection(db, 'users', user.uid, 'chatHistory');
              const q = query(chatHistoryRef, orderBy('lastAccessed', 'desc'));
              const querySnapshot = await getDocs(q);
              
              if (!querySnapshot.empty) {
                const firstChat = querySnapshot.docs[0].data();
                const isMobile = window.innerWidth <= 968;
                const gender = firstChat.gender || 'girls';
                const chatUrl = `/${gender}/${encodeURIComponent(firstChat.modelName)}${isMobile ? '?showChatHistory=true' : ''}`;
                setIsNavigating(true);
                router.push(chatUrl);
              } else {
                alert('No chat history found. Start a conversation first!');
              }
            } catch (error) {
              console.error('Error fetching chat history:', error);
              alert('Error loading chat history');
            }
          };
          fetchFirstChat();
        }
        break;
      case 'createImage':
        setIsNavigating(true);
        router.push('/imagegenertion');
        break;
      case 'myai':
        if (!user) {
          alert('Please sign in to access My AI');
        } else {
          setIsNavigating(true);
          router.push('/myai');
        }
        break;
      case 'vip':
        setIsNavigating(true);
        router.push('/vip');
        break;
      default:
        break;
    }
  };

  return (
    <html lang="en">
      <head>
        {/* Google Tag Manager */}
        <Script
          id="google-tag-manager"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-KC9QCSH7');`,
          }}
        />
        
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <title>Lovleybabe.ai - AI Character Platform</title>
        <meta name="description" content="Connect with AI characters on Lovleybabe.ai" />
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
      </head>
      <body className={`${geistSans.variable} ${geistMono.variable}`}>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe 
            src="https://www.googletagmanager.com/ns.html?id=GTM-KC9QCSH7"
            height="0" 
            width="0" 
            style={{display: 'none', visibility: 'hidden'}}
          />
        </noscript>

        {/* Google Analytics */}
        <Script
          strategy="afterInteractive"
          src="https://www.googletagmanager.com/gtag/js?id=G-YW0PVGZB58"
        />
        <Script
          id="google-analytics"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-YW0PVGZB58');
            `,
          }}
        />

        {/* TrafficStars Click ID Tracker */}
        <Suspense fallback={null}>
          <TrafficStarsTracker />
        </Suspense>

        {children}

        {/* Mobile Bottom Navigation - Root Level Fixed Bar */}
        {isMounted && (
        <nav 
          className="mobile-bottom-nav"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            top: 'auto',
            width: '100vw',
            maxWidth: '100vw',
            zIndex: 2147483647,
            transform: 'translateZ(0) translate3d(0, 0, 0)',
          }}
        >
          <div className="mobile-bottom-nav-items">
            <button
              className={`mobile-bottom-nav-item ${activeBottomNav === 'explore' ? 'active' : ''}`}
              onClick={() => handleBottomNavClick('explore')}
              disabled={isNavigating}
            >
              <i className="fas fa-compass"></i>
              <span>Explore</span>
            </button>

            <button
              className={`mobile-bottom-nav-item ${activeBottomNav === 'chat' ? 'active' : ''}`}
              onClick={() => handleBottomNavClick('chat')}
              disabled={isNavigating}
            >
              <i className="fas fa-comment"></i>
              <span>Chat</span>
            </button>
            <button
              className={`mobile-bottom-nav-item vip ${activeBottomNav === 'vip' ? 'active' : ''}`}
              onClick={() => handleBottomNavClick('vip')}
              disabled={isNavigating}
            >
              <i className="fas fa-gem"></i>
              <span>VIP</span>
            </button>

            <button
              className={`mobile-bottom-nav-item ${activeBottomNav === 'createImage' ? 'active' : ''}`}
              onClick={() => handleBottomNavClick('createImage')}
              disabled={isNavigating}
            >
              <i className="fas fa-image"></i>
              <span>Image</span>
            </button>

            <button
              className={`mobile-bottom-nav-item ${activeBottomNav === 'myai' ? 'active' : ''}`}
              onClick={() => handleBottomNavClick('myai')}
              disabled={isNavigating}
            >
              <i className="fas fa-heart"></i>
              <span>My AI</span>
            </button>

           
          </div>
        </nav>
        )}

        {/* TrafficJunky Infinity Tag */}
        <Script
          src="https://fl-cdn2.contentabc.com/delivery/idsync/idsync.min.js"
          strategy="lazyOnload"
        />
        <Script
          id="trafficjunky-infinity"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
              document.addEventListener("DOMContentLoaded", function (event) {
                if (window.idsync && typeof window.idsync.config === 'function') {
                  try {
                    window.idsync.config(37077)
                      .fp_get()
                      .sync();
                  } catch {
                  }
                }
              });
            `,
          }}
        />
      </body>
    </html>
  );
}
